import { all, call, put, takeLatest, select } from "redux-saga/effects"
import { actionTypes, makeAction } from "../actions"
import produce from "immer"
import { isset, isNotNullOrEmpty } from "../../utils/validate"
import { loadConfigApi, loadProductApi, loadCheckOrderApi, loadCheckLocationApi, loadSSOApi, incrementFailedTryCountApi } from "../apis"
import { head, path } from "ramda"
import { loadCountries } from "./region"
import { convertOrderProducts, convertProductDetails } from "./landing"

export let mainSagas = [
  takeLatest(actionTypes.APP_CHECK_SSO, checkSSO),
  takeLatest(actionTypes.APP_CHECK_LOCATION, checkLocation),
  takeLatest(actionTypes.APP_INIT_LANDING, initLanding),
  takeLatest(actionTypes.APP_INIT_DETAIL, initDetail),
  takeLatest(actionTypes.APP_INIT_SUMMARY_RELATED, initSummaryRelated),
  takeLatest(actionTypes.APP_INCREMENT_FAILED_TRY_COUNT, incrementFailedTryCount)
]

const initialState = {
  locale: "en",
  packageDetails: {
    error: false
  },
  skipCheckOrder: false,
  showPackageDetailsModal: false,
  fields: {},
  hashedPackageID: "",
  currentLocationInfo: {},
  appConfig: {},
  query: {}
}

const subActions = {
  SET_PACKAGE_DETAILS: "SET_PACKAGE_DETAILS",
  SET_LOCATION_INFO: "SET_LOCATION_INFO",
  SET_HASHED_PACKAGE_ID: "SET_HASHED_PACKAGE_ID",
  SET_APP_CONFIG: "SET_APP_CONFIG",
  SET_QUERIES: "SET_QUERIES",
  LOAD_PACKAGE_DETAILS_START: "LOAD_PACKAGE_DETAILS_START",
  LOAD_PACKAGE_DETAILS_SUCCESS: "LOAD_PACKAGE_DETAILS_SUCCESS",
  LOAD_PACKAGE_DETAILS_FAILED: "LOAD_PACKAGE_DETAILS_FAILED"
}

export default (state = initialState, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case actionTypes.APP_SKIP_COMING_CHECK_ORDER:
        draft.skipCheckOrder = action.payload.skip
        break
      case actionTypes.APP_SET_LOADING:
        Object.assign(draft, action.payload)
        break
      case actionTypes.APP_SET_PACKAGE_DETAILS_MODAL_SHOW:
        Object.assign(draft, action.payload)
        break
      case subActions.SET_QUERIES:
        Object.assign(draft, action.payload)
        break
      case actionTypes.PACKAGE_DETAILS_SET:
        Object.assign(draft, action.payload)
        break
      case subActions.SET_HASHED_PACKAGE_ID:
        Object.assign(draft, action.payload)
        break
      case subActions.SET_APP_CONFIG:
        Object.assign(draft, action.payload)
        break
      case subActions.SET_LOCATION_INFO:
        Object.assign(draft, action.payload)
        break
      case subActions.LOAD_PACKAGE_DETAILS_START:
        Object.assign(draft.packageDetails, action.payload)
        break
      case subActions.LOAD_PACKAGE_DETAILS_SUCCESS:
        Object.assign(draft.packageDetails, action.payload)
        break
      case subActions.LOAD_PACKAGE_DETAILS_FAILED:
        Object.assign(draft.packageDetails, action.payload)
        break
    }
  })

const setCountryOptions = (options) => makeAction(actionTypes.REGION_SET_COUNTRY_OPTIONS, { countryOptions: options })
const setPackageDetails = (details) => makeAction(actionTypes.PACKAGE_DETAILS_SET, { packageDetails: details })

const loadPackageDetailStart = (hashedID) => makeAction(subActions.LOAD_PACKAGE_DETAILS_START, { hashedID, error: false })
const loadPackageDetailSuccess = (result) => makeAction(subActions.LOAD_PACKAGE_DETAILS_SUCCESS, { ...result })
const loadPackageDetailFailed = (statusCode, error) => makeAction(subActions.LOAD_PACKAGE_DETAILS_FAILED, { statusCode, error })

const loadCheckOrderStart = (orderID) => makeAction(actionTypes.LANDING_LOAD_CHECK_ORDER_START, { orderID, error: false })
const loadCheckOrderSuccess = (order) => makeAction(actionTypes.LANDING_LOAD_CHECK_ORDER_SUCCESS, { ...order })
const loadCheckOrderFailed = (statusCode, error) => makeAction(actionTypes.LANDING_LOAD_CHECK_ORDER_FAILED, { statusCode, error })

function* loadProduct(hashedID) {
  log("Load Product Start")
  yield put(loadPackageDetailStart(hashedID))
  try {
    const params = {
      t: new Date()
    }
    const { data: productDetail } = yield call(loadProductApi, hashedID, params)
    log("product")
    log(productDetail)
    yield put(loadPackageDetailSuccess(productDetail))
  } catch (err) {
    log(`Error ${err}`)
    const statusCode = path(["response", "status"], err)
    yield put(loadPackageDetailFailed(statusCode, err))
  }
  log("Load Product End")
}

function* loadConfig() {
  log("Load Config Start")
  try {
    const { data: config } = yield call(loadConfigApi)
    log("config")
    log(config)
    if (isNotNullOrEmpty(config)) {
      yield put(makeAction(subActions.SET_APP_CONFIG, { appConfig: config }))
    }
  } catch (err) {
    log(`Error ${err}`)
  }
  log("Load Config End")
}

function* initLanding({ resolve, reject, payload: { query } }) {
  log("Init Landing Start")
  const { hashedID } = query
  try {
    yield put(makeAction(subActions.SET_QUERIES, { query }))
    const callLoadConfig = call(loadConfig)
    const callLoadProduct = call(loadProduct, hashedID)
    yield all([callLoadConfig, callLoadProduct])
    resolve && resolve()
  } catch (err) {
    log(`Error ${err}`)
    reject && reject()
  }
  log("Init Landing End")
}

function* initDetail({ resolve, reject, payload }) {
  log("Init Detail Start")
  try {
    const callLoadConfig = call(loadConfig)
    const callLoadCountries = call(loadCountries, {})

    yield all([callLoadConfig, callLoadCountries])
    resolve && resolve()
  } catch (err) {
    log(`Error ${err}`)
    reject && reject()
  }
  log("Init Detail End")
}

function convertCurrency(orderPackage, order) {
  if (orderPackage.currency?.length) return orderPackage.currency
  if (order?.customer?.countryCode == "HKG") return "HKD"
  if (order?.customer?.countryCode == "CHN") return "RMB"
  if (order?.customer?.countryCode?.length) return "USD"
  return undefined
}

export function* updatePackageDetailsByOrder(order) {
  log("Update Package Details By Order Start")
  if (order && isNotNullOrEmpty(convertOrderProducts(order))) {
    const firstOrderPackage = head(convertOrderProducts(order))
    const nextPackageDetails = {
      ...convertProductDetails(firstOrderPackage),
      amount: firstOrderPackage.amount,
      currency: convertCurrency(firstOrderPackage, order)
    }
    log("nextPackageDetails")
    log(nextPackageDetails)
    yield put(setPackageDetails(nextPackageDetails))
  }
  log("Update Package Details By Order End")
}

function* loadCheckOrder(orderID, params) {
  log("Load Check Order Start")
  yield put(loadCheckOrderStart(orderID))
  try {
    const { data: checkedOrder } = yield call(loadCheckOrderApi, orderID, params)
    log(checkedOrder)
    yield put(loadCheckOrderSuccess(checkedOrder))
    if (checkedOrder?.orderPayment?.paymentDetails?.paymentMethod) {
      yield put(makeAction(actionTypes.LANDING_CHOSE_PAYMENT_METHOD, { paymentMethod: checkedOrder.orderPayment.paymentDetails.paymentMethod }))
    }
    yield call(updatePackageDetailsByOrder, checkedOrder)
  } catch (err) {
    log(`Error ${err}`)
    const statusCode = path(["response", "status"], err)
    yield put(loadCheckOrderFailed(statusCode, err))
  }
  log("Load Check Order End")
}

function* initSummaryRelated({ resolve, reject, payload: { orderID, otaToken } }) {
  log("Init Summary Related Start")
  try {
    const params = { t: `${Math.random()}`, otaToken }
    let promises = [call(loadConfig)]
    //const callLoadConfig = call(loadConfig)
    const skipCheckOrder = yield select((state) => state.main.skipCheckOrder)
    if (skipCheckOrder) {
      yield put(makeAction(actionTypes.APP_SKIP_COMING_CHECK_ORDER, { skip: false }))
    } else {
      promises.push(call(loadCheckOrder, orderID, params))
    }
    yield all(promises)
    resolve && resolve()
  } catch (err) {
    log(`Error ${err}`)
    reject && reject()
  }
  log("Init Summary Related End")
}

function* checkSSO(action) {
  log("Check SSO Start")
  const { token } = action.payload
  log("token " + token)
  if (!isset(token)) return

  try {
    const { data: json } = yield call(loadSSOApi, token)
    log(json)

    if (isNotNullOrEmpty(json) && json.basicProfile) {
      const { company: nameEN = "", emailId: email = "" } = json.basicProfile
      const { ssouid: SSOUID = "" } = json
      yield put(makeAction(actionTypes.LANDING_SET_COMPANY_LANDING_INFO, { emailFromSSO: email, nameENFromSSO: nameEN, nameEN, email, SSOUID, fromSSO: true }))
    }
  } catch (err) {
    log(`Error ${err}`)
  }
  log("Check SSO End")
}

function* checkLocation(action) {
  log("Check IP Location Start")
  const { resolve, reject } = action
  try {
    const { data: json } = yield call(loadCheckLocationApi)
    log(json)
    if (isNotNullOrEmpty(json)) {
      yield put(makeAction(subActions.SET_LOCATION_INFO, { currentLocationInfo: json }))
    }
    if (resolve) resolve()
  } catch (err) {
    log(`Error ${err}`)
    if (reject) reject()
  }
  log("Check IP Location End")
}

function* incrementFailedTryCount({ resolve, reject, payload: { orderID, checkOrderID } }) {
  log("Increment Failed Try Count Start")
  try {
    const params = { t: `${Math.random()}` }
    const { data: json } = yield call(incrementFailedTryCountApi, orderID, params)
    log(json)
    yield call(loadCheckOrder, checkOrderID, params)
    resolve && resolve()
  } catch (err) {
    log(`Error ${err}`)
    reject && reject()
  }
  log("Increment Failed Try Count End")
}

function log(text) {
  console.log("%c Main ", "color: white; background-color: #95B46A", text)
}
